import { DateTime } from 'luxon';
import { check, memoize, type } from '@mews-framework/utils';
import { accessible, comparable, localizable, manipulable } from '../componentEnhancers';
import { DateTimePart, ISOFormat, LocalizationMethod } from '../enums';
import Month from '../months/month';
import { toLuxon } from '../utils';
import Date from './date';

const PARTS = [DateTimePart.Year, DateTimePart.Month, DateTimePart.Date, DateTimePart.Week, DateTimePart.Day] as const;

const FORMATS = [
    [LocalizationMethod.String, 'Date'],
    [LocalizationMethod.ShortString, 'DateShort'],
    [LocalizationMethod.LongString, 'DateLong'],
    [LocalizationMethod.ShortMonthDayString, 'DateShortMonthDay'],
] as const;

export const toDateLuxon = memoize((date: Date) => {
    type.check(date, Date);
    return DateTime.fromObject({ ...date.internal }, { zone: date.timezone });
});

export default function DateComponent() {
    return type.create(DateComponent, DateService);
}

export const DateService = {
    ...comparable(toDateLuxon),
    ...manipulable(Date, toDateLuxon, PARTS),
    ...accessible(toDateLuxon, PARTS),
    ...localizable(Date, toDateLuxon, FORMATS),
    parse(dateString: string, timezone: string) {
        check.string(dateString);
        check.timezone(timezone);
        check.notUtcTimezone(timezone);

        const m = toLuxon(dateString, ISOFormat.Date, timezone);
        return Date(m);
    },

    today(timezone: string) {
        check.timezone(timezone);
        check.notUtcTimezone(timezone);

        const luxon = DateTime.fromObject({}, { zone: timezone });
        return Date(luxon);
    },

    toMonth(date: Date) {
        return Month(toDateLuxon(date));
    },

    toISOString(date: Date) {
        const luxonDate = toDateLuxon(date);
        return luxonDate.toISODate();
    },

    valueOf(date: Date) {
        return toDateLuxon(date).valueOf();
    },
};
